import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import api from "@/lib/api";
import CoverImage from "@/components/marketing/CoverImage";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "portraits", label: "Portraits" },
  { key: "weddings", label: "Weddings" },
  { key: "events", label: "Events" },
  { key: "matric-balls", label: "Matric Balls" },
  { key: "graduations", label: "Graduations" },
  { key: "sports", label: "Sports" },
  { key: "films", label: "Films" },
];

export default function Portfolio() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/projects", { params: filter === "all" ? {} : { category: filter } })
      .then(({ data }) => setProjects(data || []))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, [filter]);

  const items = useMemo(() => projects, [projects]);

  return (
    <main data-testid="portfolio-page" className="bg-ink text-ivory min-h-screen pt-32 md:pt-40">
      <section className="px-6 md:px-12 lg:px-16">
        <p className="label-mono opacity-60 line-badge">Portfolio</p>
        <h1 className="mt-6 font-editorial text-6xl md:text-8xl leading-[0.95]">
          The <em className="italic">work</em>.
        </h1>
        <p className="mt-6 max-w-xl text-ivory/70 text-lg">
          A living archive of Oereshaus projects — portraits, weddings, celebrations and film.
        </p>

        <div className="mt-12 md:mt-16 flex flex-wrap gap-3 border-y border-ivory/10 py-6">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`portfolio-filter-${f.key}`}
              className={`label-mono px-4 py-2 transition-colors duration-400 border ${
                filter === f.key
                  ? "border-ivory text-ivory bg-ivory/10"
                  : "border-transparent text-ivory/60 hover:text-ivory"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-16 pb-32 mt-16">
        {loading && (
          <p className="label-mono opacity-60" data-testid="portfolio-loading">Loading work…</p>
        )}
        {!loading && items.length === 0 && (
          <div className="py-20 text-center" data-testid="portfolio-empty">
            <p className="font-editorial text-3xl italic mb-3">Nothing here yet.</p>
            <p className="label-mono opacity-60">Projects will appear once published.</p>
          </div>
        )}
        <AnimatePresence mode="popLayout">
          <motion.div
            key={filter}
            className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-8 [column-fill:_balance]"
          >
            {items.map((p, i) => (
              <motion.div
                key={p.id || p.slug}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8, delay: (i % 6) * 0.05, ease: [0.19, 1, 0.22, 1] }}
                className="mb-4 md:mb-8 break-inside-avoid"
              >
                <Link
                  to={`/portfolio/${p.slug}`}
                  className="group block"
                  data-testid={`portfolio-item-${p.slug}`}
                >
                  <div className="photo-frame natural bg-neutral-900">
                    <CoverImage
                      src={p.cover_image}
                      fallbackSrc={(p.gallery || [])[0]}
                      alt={p.alt_text || p.title}
                      title={p.title}
                      natural
                      testId={`portfolio-cover-${p.slug}`}
                    />
                  </div>
                  <div className="mt-4 flex items-baseline justify-between gap-4">
                    <div>
                      <h3 className="font-editorial italic text-2xl md:text-3xl">{p.title}</h3>
                      <p className="label-mono opacity-60 mt-1">
                        {p.category_slug?.replace("-", " ")} · {p.location}
                      </p>
                    </div>
                    <p className="label-mono opacity-60">{p.date_or_year}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </section>
    </main>
  );
}
